/**
 * Braze SDK Utility Functions
 *
 * This file provides wrapper functions for interacting with the Braze Web SDK.
 * Documentation: https://www.braze.com/docs/developer_guide/sdk_integration/
 */

import * as braze from "@braze/web-sdk";

const BRAZE_API_KEY = process.env.REACT_APP_BRAZE_API_KEY || "";
const BRAZE_BASE_URL = process.env.REACT_APP_BRAZE_BASE_URL || "";

/**
 * Initialize the Braze SDK
 *
 * @param apiKey - Your Braze API key
 * @param baseUrl - Your Braze instance URL (e.g., sdk.iad-03.braze.com)
 * @param options - Additional initialization options
 */
export const initializeBraze = (
  apiKey: string,
  baseUrl: string,
  options: Record<string, any> = {}
): void => {
  try {
    braze.initialize(apiKey, {
      baseUrl,
      enableLogging: process.env.NODE_ENV !== "production",
      ...options,
    });
    console.debug("Braze SDK initialized with baseUrl:", baseUrl);
  } catch (error) {
    console.error("Failed to initialize Braze SDK:", error);
  }
};

/**
 * Identify a user in Braze
 * This changes the user identity and should be called when a user logs in
 *
 * @param userId - The unique identifier for the user
 */
export const identifyUser = (userId: string): void => {
  try {
    braze.changeUser(userId);
    console.debug("Braze user identified:", userId);
  } catch (error) {
    console.error("Failed to identify user in Braze:", error);
  }
};

/**
 * Track a custom event in Braze
 *
 * @param eventName - Name of the event to track
 * @param properties - Optional properties to include with the event
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>
): void => {
  try {
    if (properties) {
      braze.logCustomEvent(eventName, properties);
      console.debug("Braze event tracked:", eventName, properties);
    } else {
      braze.logCustomEvent(eventName);
      console.debug("Braze event tracked:", eventName);
    }
  } catch (error) {
    console.error("Failed to track event in Braze:", error);
  }
};

/**
 * Set a custom user attribute in Braze
 *
 * @param key - The attribute key
 * @param value - The attribute value (string, number, boolean, date, array, or object)
 */
export const setUserAttribute = (
  key: string,
  value:
    | string
    | number
    | boolean
    | Date
    | Array<any>
    | Record<string, any>
    | null
): void => {
  try {
    const user = braze.getUser();
    if (user) {
      user.setCustomUserAttribute(key, value);
      console.debug("Braze user attribute set:", key, value);
    } else {
      console.warn("Braze user object is undefined");
    }
  } catch (error) {
    console.error("Failed to set user attribute in Braze:", error);
  }
};

/**
 * Request immediate data flush to Braze servers
 * This forces events and attributes to be sent immediately
 */
export const flushData = (): void => {
  try {
    braze.requestImmediateDataFlush();
    console.debug("Braze data flush requested");
  } catch (error) {
    console.error("Failed to flush data to Braze:", error);
  }
};

/**
 * Wrapper for initializing the Braze SDK
 */
export const setupBraze = async (): Promise<void> => {
  try {
    // Validate required configuration parameters
    if (!BRAZE_API_KEY || !BRAZE_BASE_URL) {
      console.warn(
        "Braze SDK initialization skipped: Missing required configuration.",
        "Make sure REACT_APP_BRAZE_API_KEY and REACT_APP_BRAZE_BASE_URL are set in your environment."
      );
      return;
    }

    // Initialize with API key and settings
    initializeBraze(BRAZE_API_KEY, BRAZE_BASE_URL, {
      enableLogging: process.env.NODE_ENV !== "production",
      allowUserSuppliedJavascript: false,
      noCookies: false,
    });

    console.debug("Braze SDK setup complete");
  } catch (error) {
    console.error("Failed to setup Braze SDK:", error);
  }
};
